import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LanguageContext } from "../../Component/LanguageContext/LanguageContext";
import { ToastContainer } from "react-toastify";
import axios from "axios";
import auth from "../../Auth/Auth";
import styles from "./CartCheckout.module.css";
import riyals from "../../Component/PaymentForm/riyals.svg";
import { useCart } from '../../context/CartContext';

const marketingBackendUrl = import.meta.env.VITE_MARKETING_BACKEND_URL;
const marketingFrontendUrl = import.meta.env.VITE_MARKETING_FRONTEND_URL;

const CartCheckout = () => {
  // Add global error handler to suppress Tabby pre-approval errors
  useEffect(() => {
    const handleError = (event) => {
      // Suppress Tabby pre-approval errors as they are expected
      if (event.message && event.message.includes('pre-approval') && event.message.includes('401')) {
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  const { direction } = useContext(LanguageContext) || { direction: 'rtl' };
  const navigate = useNavigate();
  const location = useLocation();
  const isLoggedIn = auth.isAuthenticated();

  // Use CartContext for cart data
  const { cart, loading: cartLoading, fetchCart } = useCart() || { 
    cart: { items: [], totalAmount: 0 }, 
    loading: false, 
    fetchCart: () => {} 
  };

  const [coupon, setCoupon] = useState("");
  const [couponError, setCouponError] = useState("");
  const [paymentType, setPaymentType] = useState("full");
  const [couponApplied, setCouponApplied] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [showNameError, setShowNameError] = useState(false);
  const [newMoodleUsername, setNewMoodleUsername] = useState(null);
  const [error, setError] = useState(null);

  // Function to convert numeric value to Arabic numerals
  const convertToArabicNumerals = (num) => {
    try {
      const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
      return num
        .toString()
        .split("")
        .map((digit) => arabicNumerals[parseInt(digit)] || digit)
        .join("");
    } catch (error) {
      console.error("Error converting to Arabic numerals:", error);
      return num.toString();
    }
  };

  const getItemPrice = (item) => {
    try {
      return direction === 'rtl' ? (item.priceArabic || item.price || 0) : (item.priceEnglish || item.price || 0);
    } catch (error) {
      console.error("Error getting item price:", error);
      return 0;
    }
  };

  // Calculate total amount from cart
  const cartItems = cart?.items || [];
  const totalAmount = cart?.totalAmount || cartItems.reduce((total, item) => total + parseFloat(getItemPrice(item)), 0);

  // Fetch cart on mount (in case user comes directly to checkout)
  useEffect(() => {
    try {
      fetchCart();
    } catch (error) {
      console.error("Error fetching cart on mount:", error);
      setError("Failed to load cart data");
    }
    // eslint-disable-next-line
  }, []);

  // Auto-retry cart fetch if empty after user submission
  useEffect(() => {
    if (isSubmitted && cartItems.length === 0 && !cartLoading) {
      const timer = setTimeout(() => {
        console.log("Auto-retrying cart fetch after user submission...");
        try {
          fetchCart();
        } catch (error) {
          console.error("Error in auto-retry cart fetch:", error);
        }
      }, 2000);
      
      return () => clearTimeout(timer);
    }
  }, [isSubmitted, cartItems.length, cartLoading]);

  // Debug info
  console.log("CartCheckout Debug:", {
    cartItems: cartItems.length,
    totalAmount,
    isLoggedIn,
    isSubmitted,
    userEmail: localStorage.getItem("useremailidis"),
    guestCartId: localStorage.getItem("guestCartId"),
    cartData: cart
  });

  // --- UI LOGIC REFACTOR ---
  // Show cart summary and total always, payment options only after guest info submitted or logged in
  const showPaymentOptions = isLoggedIn || isSubmitted;

  useEffect(() => {
    const fetchCartItems = async () => {
      try {
        const userEmail = localStorage.getItem("useremailidis");
        console.log("Fetching cart for user:", userEmail);

        // Get total amount from URL parameters
        const searchParams = new URLSearchParams(location.search);
        const urlTotal = searchParams.get('total');
        console.log("URL Total parameter:", urlTotal);

        if (urlTotal) {
          const parsedTotal = parseFloat(urlTotal);
          console.log("Parsed total from URL:", parsedTotal);
          if (!isNaN(parsedTotal)) {
            // setTotalAmount(parsedTotal); // This line is now redundant as totalAmount is derived from cart
          }
        }

        // setCartItems(cartItems); // This line is now redundant as cartItems is derived from cart
        // Calculate total amount
        // const totalAmount = cartItems.reduce((total, item) => total + parseFloat(getItemPrice(item)), 0);
        // setTotalAmount(totalAmount);
        setDiscountedPrice(null);
      } catch (error) {
        console.error("Error fetching cart items:", error);
        if (error.response) {
          console.error("Error response:", error.response.data);
        }
      } finally {
        // setLoading(false); // This line is now redundant as loading is derived from cartLoading
      }
    };

    // fetchCartItems(); // This useEffect is now redundant as cart is fetched on mount
  }, [navigate, location.search, cart]); // Added cart to dependency array

  const handleApplyCoupon = async () => {
    if (!coupon) {
      setCouponError(
        direction === "rtl"
          ? "يرجى إدخال كود الخصم"
          : "Please enter a coupon code."
      );
      return;
    }

    try {
      const response = await axios.get(
        `${marketingBackendUrl}/maflam/getcoupon/${coupon}`
      );
      const currentDate = new Date();
      const validUptoDate = new Date(response.data.coupon.validUptoDate);

      if (currentDate > validUptoDate) {
        setCouponError(
          direction === "rtl"
            ? "تم انتهاء صلاحية هذا الكود"
            : "This coupon has expired."
        );
        setCouponApplied(false);
        return;
      }

      if (response.data.coupon.userEmail !== "general@maflam.com") {
        if (response.data.coupon.couponState !== "Active") {
          setCouponError(
            direction === "rtl" ? "الكود غير مفعل" : "Coupon is not active."
          );
          setCouponApplied(false);
          return;
        }
      }

      const discount = response.data.coupon.percentageDiscount;
      const discountAmount = (totalAmount * parseFloat(discount)) / 100;
      const finalPrice = totalAmount - discountAmount;
//cart
      setDiscountAmount(discountAmount);
      setDiscountedPrice(finalPrice);
      setCouponError(
        direction === "rtl" ? (
          <span>
            تم تطبيق الكود بنجاح! خصم: {discountAmount.toFixed(2)}{" "} 
            <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
          </span>
        ) : (
          <span>
            Coupon Applied! Discount: {discountAmount.toFixed(2)}{" "}
            <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
          </span>
        )
      );
      localStorage.setItem("coupon", response.data.coupon.couponCode);
      setCouponApplied(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setCouponError(
          direction === "rtl" ? "الكود غير موجود" : "Coupon not found."
        );
      } else {
        setCouponError(
          direction === "rtl"
            ? "حدث خطأ غير متوقع. حاول مرة أخرى."
            : "An unexpected error occurred. Please try again."
        );
      }
      setCouponApplied(false);
    }
  };

  const handleInitialSubmit = async () => {
    const trimmedName = fullName.trim();
    const trimmedEmail = email.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const getAlertMessage = (key) => {
      const messages = {
        en: {
          fullNameRequired: "Please enter your Full Name.",
          fullNameLength: "Full name must be at least 3 characters long.",
          nameSpace: "Full name must include both first and last name.",
          emailRequired: "Email is required.",
          emailInvalid: "Invalid email format.",
        },
        ar: {
          fullNameRequired: "يرجى إدخال الاسم الكامل.",
          fullNameLength: "يجب أن يتكون الاسم الكامل من 3 أحرف على الأقل.",
          nameSpace: "يجب أن يحتوي الاسم الكامل على الاسم الأول واسم العائلة.",
          emailRequired: "البريد الإلكتروني مطلوب.",
          emailInvalid: "صيغة البريد الإلكتروني غير صحيحة.",
        },
      };
      return direction === "rtl" ? messages.ar[key] : messages.en[key];
    };

    if (!trimmedName) return alert(getAlertMessage("fullNameRequired"));
    if (trimmedName.length < 3) return alert(getAlertMessage("fullNameLength"));
    if (!trimmedName.includes(" ")) return alert(getAlertMessage("nameSpace"));
    if (!trimmedEmail) return alert(getAlertMessage("emailRequired"));
    if (!emailRegex.test(trimmedEmail))
      return alert(getAlertMessage("emailInvalid"));

    setShowNameError(false);

    const [firstName, ...rest] = trimmedName.split(" ");
    const lastName = rest.join(" ");

    try {
      // Get guest cart ID before creating user
      const guestCartId = localStorage.getItem('guestCartId');
      
      // Use the same logic as PaymentForm.jsx for guest info submission
      const response = await axios.post(
        `${marketingBackendUrl}/maflam/handle-email`,
        {
          emailId: trimmedEmail,
          firstName,
          lastName,
        }
      );

      const generatedMoodleId = response?.data?.user?.moodleId;
      const token = response?.data?.token;

      if (generatedMoodleId) {
        setNewMoodleUsername(generatedMoodleId);
        localStorage.setItem("moodleusername", generatedMoodleId);
      }

      localStorage.setItem("useremailidis", trimmedEmail);
      localStorage.setItem("fullname", trimmedName);
      // Don't set currentusername here - only set it after payment success
      // localStorage.setItem("currentusername", trimmedName); // Set currentusername for navbar display

      if (token) {
        auth.login({
          token,
          email: trimmedEmail,
          fullName: trimmedName,
          // Don't set usernameInEng here - only set it after payment success
          // usernameInEng: trimmedName, // Add usernameInEng for navbar fallback
          moodleusername: generatedMoodleId,
        });
      }

      // Migrate guest cart to user cart if guest cart exists
      if (guestCartId) {
        try {
          console.log("Migrating guest cart to user cart...");
          const migrationResponse = await axios.post(`${marketingBackendUrl}/maflam/cart/migrate`, {
            guestCartId,
            userEmail: trimmedEmail
          });
          console.log("Guest cart migrated successfully:", migrationResponse.data);
          
          // Show success message if items were migrated
          if (migrationResponse.data.migratedItems > 0) {
            if (direction === "rtl") {
              console.log(`تم نقل ${migrationResponse.data.migratedItems} دورة بنجاح إلى حسابك`);
            } else {
              console.log(`Successfully migrated ${migrationResponse.data.migratedItems} courses to your account`);
            }
          }
          
          // Clear guest cart ID from localStorage
          localStorage.removeItem('guestCartId');
          
          // Add a small delay to ensure migration is complete
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (migrationError) {
          console.error("Error migrating guest cart:", migrationError);
          // Show user-friendly error message
          if (direction === "rtl") {
            alert("تم إنشاء حسابك بنجاح، لكن حدث خطأ في نقل محتويات السلة. يرجى إضافة الدورات مرة أخرى.");
          } else {
            alert("Your account was created successfully, but there was an error transferring your cart items. Please add the courses again.");
          }
          // Don't fail the entire process if migration fails
        }
      }

      // Immediately fetch the cart after login/migration
      await fetchCart();

      const currentUser = auth.getAuthData();
      console.log("Current user after login:", currentUser);

      setIsSubmitted(true);
      console.log("handle-email response:", response.data);
    } catch (error) {
      console.error(
        "Error submitting user info:",
        error.response?.data || error.message
      );
      alert(direction === "rtl" ? "حدث خطأ غير متوقع عند إرسال المعلومات." : "An unexpected error occurred while submitting information.");
    }
  };

  const getToken = () => {
    // Try to get token from auth context or localStorage
    const userDetails = auth.getAuthData();
    return userDetails?.token || localStorage.getItem('token');
  };

  const handleCheckout = async () => {
    if (isProcessing) {
      return;
    }

    // Always get token for both user types
    const token = getToken();
    if (!token) {
      alert(direction === 'rtl' ? 'يرجى إدخال الاسم والبريد الإلكتروني أولاً.' : 'Please enter your name and email first.');
      return;
    }

    try {
      setIsProcessing(true);
      
      const userDetails = auth.getAuthData();
      const token = userDetails?.token;

      // Do not redirect to login if no token; allow guest payment as in PaymentForm.jsx

      const finalAmount = discountedPrice !== null ? discountedPrice : totalAmount;

      if (!cartItems || cartItems.length === 0) {
        console.error('No items in cart');
        alert(direction === "rtl" ? "السلة فارغة" : "Cart is empty");
        return;
      }

      // Store cart items in localStorage for email function
      const cartItemsForEmail = cartItems.map(item => ({
        courseNameArabic: item.nameArabic || item.courseNameArabic || item.name || item.description,
        courseNameEnglish: item.nameEnglish || item.courseNameEnglish || '',
        courseCostEnglish: item.priceEnglish !== undefined ? item.priceEnglish : item.price,
        courseCostArabic: item.priceArabic !== undefined ? item.priceArabic : item.price
      }));
      
      localStorage.setItem("cartItems", JSON.stringify(cartItemsForEmail));
      localStorage.setItem("totalAmount", finalAmount.toString());
      localStorage.setItem("currency", "SAR");
      
      // Store course names for email fallback (in case cart items are lost)
      if (cartItems.length > 0) {
        const firstCourse = cartItems[0];
        localStorage.setItem("coursenamearabic", firstCourse.name || firstCourse.courseNameArabic || firstCourse.description);
        localStorage.setItem("coursenameenglish", firstCourse.name || firstCourse.courseNameEnglish || firstCourse.description);
        
        // Store all course names for multiple courses
        if (cartItems.length > 1) {
          const allArabicNames = cartItems.map(item => item.nameArabic || item.courseNameArabic || item.name || item.description).join(', ');
          const allEnglishNames = cartItems.map(item => item.nameEnglish || item.courseNameEnglish || '').join(', ');
          localStorage.setItem("coursenamearabic", allArabicNames);
          localStorage.setItem("coursenameenglish", allEnglishNames);
        }
      }
      
      console.log("Stored cart items in localStorage:", cartItemsForEmail);

      // Get all productIds from cart items
      const productIds = cartItems.map(item => item.productId);

      // Fix: Remove trailing slash from base URL before appending path
      const cleanFrontendUrl = marketingFrontendUrl.replace(/\/$/, "");
      const requestBody = {
        amount: finalAmount,
        courseIds: productIds, // Send all course IDs
        cuponCode: coupon || "",
        callbackTabby: `${cleanFrontendUrl}/success3`,
        callback: `${cleanFrontendUrl}/success2`,
        return: `${cleanFrontendUrl}/success2`,
        token, // Always include token in body
        paymentType: paymentType === "PAY_LATER" ? "PAY_LATER" : "CARD"
      };

      console.log('Payment Request:', {
        ...requestBody,
        courseIds: productIds
      });

      const apiUrl = paymentType === "PAY_LATER" 
        ? `${marketingBackendUrl}/maflam/create-session`
        : `${marketingBackendUrl}/maflam/paymenttransction`;

      const response = await axios.post(apiUrl, requestBody, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response?.data) {
        const authData = auth.getAuthData() || {};
        authData.paymentData = response.data;
        auth.login(authData);

        if (paymentType === "PAY_LATER") {
          localStorage.setItem("payment_id", response.data.response.payment.id);
          
          // Get the web_url from the response
          const webUrl = response.data.response.configuration.available_products.installments[0].web_url;
          
          if (webUrl && webUrl !== "null") {
            // Add error handling for Tabby pre-approval calls
            window.addEventListener('error', function(e) {
              // Suppress Tabby pre-approval errors as they are expected
              if (e.message && e.message.includes('pre-approval') && e.message.includes('401')) {
                e.preventDefault();
                return false;
              }
            });
            
            window.location.href = webUrl;
          } else {
            console.error('Invalid Tabby web_url received:', webUrl);
            alert(direction === "rtl" ? "خطأ في إنشاء جلسة الدفع" : "Error creating payment session");
          }
        } else {
          const transId = response?.data?.data?.tran_ref || "null";
          localStorage.setItem("tran_ref", transId);
          window.location.href = response.data.data?.redirect_url;
        }
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Payment failed';
      alert(direction === "rtl" ? errorMessage : errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  if (cartLoading) {
    return (
      <div className={styles.maincontainer} style={{ direction }}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ color: '#fff', fontSize: '1.2rem' }}>
                {direction === "rtl" ? "جاري تحميل السلة..." : "Loading cart..."}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.maincontainer} style={{ direction }}>
        <div className={styles.container}>
          <div className={styles.card}>
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ color: '#ff6b6b', fontSize: '1.2rem', marginBottom: '1rem' }}>
                {direction === "rtl" ? "حدث خطأ في تحميل السلة" : "Error loading cart"}
              </div>
              <button
                onClick={() => {
                  setError(null);
                  fetchCart();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#39FFFB',
                  color: '#000',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                {direction === "rtl" ? "إعادة المحاولة" : "Retry"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const finalPrice = discountedPrice !== null ? discountedPrice : totalAmount;
  const isGuest = !isLoggedIn && !isSubmitted;

  return (
    <div className={styles.maincontainer} style={{ direction }}>
      <div className={styles.container}>
        <div className={styles.card}>
          <h2 className={styles.title}>
            {direction === "rtl" ? "ملخص الطلب" : "Order Summary"}
          </h2>
          <div className={styles.cartItems}>
            {cartItems && cartItems.length > 0 ? (
              cartItems.map((item, index) => {
                try {
                  return (
                    <div key={index} className={styles.cartItem}>
                      <div className={styles.courseInfo}>
                        <div className={styles.courseThumbnail}>
                          <img
                            src={item.thumbnail || item.image || "https://maflamlmsnew.s3.me-central-1.amazonaws.com/marketingphotos/default-course.png"}
                            alt={direction === 'rtl'
                              ? (item.courseNameArabic || item.nameArabic || item.courseName || item.name)
                              : (item.courseNameEnglish || item.nameEnglish || item.courseName || item.name)}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "https://maflamlmsnew.s3.me-central-1.amazonaws.com/marketingphotos/default-course.png";
                            }}
                          />
                        </div>
                        <div className={styles.courseDetails}>
                          <div className={styles.courseName}>
                            {direction === 'rtl'
                              ? (item.courseNameArabic || item.nameArabic || item.courseName || item.name)
                              : (item.courseNameEnglish || item.nameEnglish || item.courseName || item.name)}
                          </div>
                          <div className={styles.courseDescription}>
                            {(direction === 'rtl'
                              ? (item.descriptionArabic || item.description)
                              : (item.descriptionEnglish || item.description)) || (direction === 'rtl' ? "لا يوجد وصف متاح" : "No description available")}
                          </div>
                        </div>
                      </div>
                      <div className={styles.coursePrice}>
                        {direction === 'rtl' ? (
                          <>
                            {convertToArabicNumerals(Number(getItemPrice(item)).toFixed(2))} <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                          </>
                        ) : (
                          <>
                            {getItemPrice(item)} <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                          </>
                        )}
                      </div>
                    </div>
                  );
                } catch (itemError) {
                  console.error("Error rendering cart item:", itemError, item);
                  return null;
                }
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#fff' }}>
                {direction === "rtl" ? "السلة فارغة" : "Cart is empty"}
              </div>
            )}
          </div>
          
          {/* Refresh cart button for debugging */}
          {isSubmitted && cartItems.length === 0 && (
            <div style={{ textAlign: 'center', margin: '20px 0', padding: '20px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <p style={{ marginBottom: '15px', color: '#666' }}>
                {direction === "rtl" 
                  ? "لم يتم العثور على دورات في السلة. يرجى المحاولة مرة أخرى." 
                  : "No courses found in cart. Please try again."}
              </p>
              <button
                onClick={() => {
                  console.log("Manual cart refresh triggered");
                  fetchCart();
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#39FFFB',
                  color: '#000',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {direction === "rtl" ? "تحديث السلة" : "Refresh Cart"}
              </button>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
                {direction === "rtl" 
                  ? "إذا استمرت المشكلة، يرجى إضافة الدورات مرة أخرى" 
                  : "If the problem persists, please add courses again"}
              </div>
            </div>
          )}

          {discountAmount > 0 && (
            <div className={styles.discountRow}>
              <div className={styles.discountLabel}>
                {direction === "rtl" ? "الخصم" : "Discount"}
              </div>
              <div className={styles.discountAmount}>
                {direction === "rtl" ? (
                  <>
                    - {convertToArabicNumerals(discountAmount.toFixed(2))}{" "}
                    <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                  </>
                ) : (
                  <>
                    - {discountAmount.toFixed(2)}{" "}
                    <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                  </>
                )}
              </div>
            </div>
          )}

          <div className={styles.totalAmount}>
            <div className={styles.totalLabel}>
              {direction === "rtl" ? "المبلغ الإجمالي" : "Total Amount"}:
            </div>
            <div className={styles.totalValue}>
              {direction === "rtl" ? (
                <>
                  {convertToArabicNumerals(Number(finalPrice).toFixed(2))} {" "}
                  <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                </>
              ) : (
                <>
                  {finalPrice.toFixed(2)} {" "}
                  <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                </>
              )}
            </div>
          </div>

          {/* Show guest info form if not logged in and not submitted */}
          {!showPaymentOptions && (
            <>
              <h3 style={{ marginTop: 24, marginBottom: 8 }}>
                {direction === "rtl" ? "أدخل اسمك الكامل والبريد الإلكتروني" : "Enter your Full Name and Email"}
              </h3>
              <div className={styles.formWrapper}>
                <div className={styles.guestInfoRow}>
                  <input
                    type="text"
                    placeholder={direction === "rtl" ? "الاسم الكامل" : "Full Name"}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                  />
                  <input
                    type="text"
                    placeholder={direction === "rtl" ? "البريد الإلكتروني" : "Email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button
                    onClick={handleInitialSubmit}
                  >
                    {direction === "rtl" ? "إرسال" : "Submit"}
                  </button>
                </div>
                {showNameError && (
                  <p style={{ color: "red", marginTop: "10px" }}>
                    {direction === "rtl"
                      ? "يرجى إدخال الاسم الكامل (الاسم الأول واسم العائلة) والبريد الإلكتروني."
                      : "Please enter your full name (first and last name) and email."}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Show payment options and coupon only after guest info is submitted or logged in */}
          {showPaymentOptions && (
            <>
              <div className={styles.paymentOptions}>
                <p>
                  {direction === "rtl"
                    ? "اختر طريقة الدفع"
                    : "Choose Payment Method"}
                </p>
                <div className={styles.paymentOption}>
                  <label>
                    <input
                      type="radio"
                      name="paymentType"
                      value="full"
                      checked={paymentType === "full"}
                      onChange={() => setPaymentType("full")}
                    />
                    {direction === "rtl"
                      ? "دفع كامل أو Apple Pay"
                      : "Payment By Credit/Debit Card or Apple Pay"}
                  </label>
                </div>
                <div className={styles.paymentOption}>
                  <label>
                    <input
                      type="radio"
                      name="paymentType"
                      value="PAY_LATER"
                      checked={paymentType === "PAY_LATER"}
                      onChange={() => setPaymentType("PAY_LATER")}
                    />
                    {direction === "rtl" ? "الدفع بالتقسيط" : "Pay Later"}
                  </label>
                </div>
              </div>

              <div className={styles.coupon}>
                <label>
                  {direction === "rtl"
                    ? "أدخل كود الخصم، إذا كان لديك"
                    : "Enter the discount code if you have one"}
                </label>
                <div className={styles.couponInput}>
                  <input
                    type="text"
                    placeholder={
                      direction === "rtl"
                        ? "أدخل كود الخصم"
                        : "Enter coupon code"
                    }
                    value={coupon}
                    onChange={(e) => setCoupon(e.target.value)}
                  />
                  <button
                    className={styles.applyCouponBtn}
                    onClick={handleApplyCoupon}
                  >
                    {direction === "rtl" ? "تطبيق" : "Apply"}
                  </button>
                </div>
                {couponError && <p className={styles.error}>{couponError}</p>}
              </div>

              <button 
                className={styles.payBtn} 
                onClick={handleCheckout}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  direction === "rtl" ? "جاري المعالجة..." : "Processing..."
                ) : (
                  direction === "rtl" ? (
                    <>
                      دفع {convertToArabicNumerals(Number(finalPrice).toFixed(2))} {" "}
                      <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                    </>
                  ) : (
                    <>
                      PAY {finalPrice.toFixed(2)} {" "}
                      <img src={riyals} alt="SAR" className={styles.riyalsIcon} />
                    </>
                  )
                )}
              </button>

              <div className={styles.paymentIcons}>
                <img
                  src="https://maflamlmsnew.s3.me-central-1.amazonaws.com/marketingphotos/checkOut1.png"
                  alt=""
                  style={{ maxHeight: "30px", borderRadius: "4px" }}
                />
                <img
                  src="https://maflamlmsnew.s3.me-central-1.amazonaws.com/marketingphotos/checkOut2.png"
                  alt=""
                  style={{ maxHeight: "30px", borderRadius: "4px" }}
                />
                <img
                  src="https://maflamlmsnew.s3.me-central-1.amazonaws.com/marketingphotos/checkOut3.png"
                  alt=""
                  style={{ maxHeight: "30px", borderRadius: "4px" }}
                />
                <img
                  src="https://financialit.net/sites/default/files/tabby_owler_20191111_120909_original.png"
                  alt=""
                  style={{
                    maxHeight: "30px",
                    borderRadius: "4px",
                    filter: "brightness(0) invert(1)",
                  }}
                />
                <img
                  src="https://th.bing.com/th/id/R.f1fdb528fc7e2576ec43a45d1a13aaa9?rik=NFaDBHem%2f7je1Q&riu=http%3a%2f%2fallvectorlogo.com%2fimg%2f2016%2f11%2fapple-pay-logo.png&ehk=fZLjSLYNLUXj2XmZ9eBV0f%2fHng2y%2bjVwSwh7t1DOtAU%3d&risl=&pid=ImgRaw&r=0"
                  alt=""
                  style={{ maxHeight: "30px", borderRadius: "4px" }}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default CartCheckout; 